/**
 * Grid Manager - Unified Grid Management System
 *
 * Centralized management system for Kendo Grids with plugin architecture.
 * Replaces scattered GridPersistence, KendoHelpers.Grid, and other grid-related functionality.
 *
 * Features:
 * - Plugin-based architecture for extensibility
 * - Unified API for all grid operations
 * - Automatic grid detection and initialization
 * - Built-in plugins: persistence, toggle handlers, filters
 * - Easy to extend with new functionality
 */

import { CONFIG } from '../../core/config.js';
import eventBus, { EVENTS } from '../../core/event-bus.js';
import { logError, logWarning, logDebug, safeExecute } from '../../core/error-handler.js';
import { GridPersistence } from '../grid-state/persistor.js';
import { FilterManager } from '../filter/manager.js';
import { showDangerConfirmationDialog, showWarningConfirmationDialog } from '../confirmation-dialog.js';

/**
 * Base plugin class for GridManager
 */
class GridPlugin {
    constructor(name, options = {}) {
        this.name = name;
        this.options = options;
        this.enabled = true;
    }

    /**
     * Initialize the plugin
     * @param {GridManager} manager - The GridManager instance
     * @param {string} selector - Grid selector
     * @returns {Promise<boolean>} - Success status
     */
    async initialize() {
        throw new Error(`Plugin ${this.name} must implement initialize method`);
    }

    /**
     * Clean up plugin resources
     */
    destroy() {
        // Override in subclasses if needed
    }

    /**
     * Enable/disable the plugin
     * @param {boolean} enabled
     */
    setEnabled(enabled) {
        this.enabled = enabled;
    }

    /**
     * Shared AJAX POST helper for grid plugin actions.
     * Handles button disable/enable, Result notification, and error handling.
     * @param {Object} ajaxOptions - jQuery AJAX options (url, type, data, contentType, etc.)
     * @param {Object} [callbacks] - Optional callbacks
     * @param {Function} [callbacks.onSuccess] - Called with the result on success
     * @param {Function} [callbacks.onError] - Called with the xhr on error (after default handling)
     * @param {Function} [callbacks.onComplete] - Called after complete (after default handling)
     * @param {jQuery}  [callbacks.$btn] - Button to disable during the request
     */
    static ajaxAction(ajaxOptions, callbacks = {}) {
        const { $btn, onSuccess, onError, onComplete } = callbacks;

        if ($btn) {
            $btn.prop('disabled', true);
        }

        $.ajax({
            ...ajaxOptions,
            success: result => {
                if (window.Result && window.Result.notifyFromResult) {
                    window.Result.notifyFromResult(result);
                }
                if (onSuccess) onSuccess(result);
            },
            error: xhr => {
                if (window.Result && window.Result.handleAjaxError) {
                    window.Result.handleAjaxError(xhr);
                }
                if (onError) onError(xhr);
            },
            complete: () => {
                if ($btn) {
                    $btn.prop('disabled', false);
                }
                if (onComplete) onComplete();
            }
        });
    }
}

/**
 * Persistence Plugin - Handles grid state persistence
 */
class PersistencePlugin extends GridPlugin {
    constructor(options = {}) {
        super('persistence', {
            enabled: CONFIG.GRID?.PERSISTENCE_ENABLED !== false,
            ...options
        });
        // Override this.enabled from options since base class ignores it
        this.enabled = this.options.enabled;
    }

    async initialize(manager, selector) {
        if (!this.enabled) {
            logDebug('PersistencePlugin: Disabled by config');
            return false;
        }

        return safeExecute(() => {
            logDebug('PersistencePlugin: Attaching to', { selector });
            GridPersistence.attach(selector);
            return true;
        }, false);
    }
}

/**
 * Toggle Handler Plugin - Handles toggle badge interactions
 */
class ToggleHandlerPlugin extends GridPlugin {
    constructor(options = {}) {
        super('toggleHandler', {
            enabled: true,
            containerSelector: document,
            ...options
        });
        // Override this.enabled from options since base class ignores it
        this.enabled = this.options.enabled;
    }

    async initialize(manager, selector) {
        if (!this.enabled || !this.options.postUrl) {
            logDebug('ToggleHandlerPlugin: Disabled or no postUrl provided');
            return false;
        }

        return safeExecute(() => {
            logDebug('ToggleHandlerPlugin: Wiring toggle handlers', {
                selector,
                postUrl: this.options.postUrl
            });

            // Wire toggle badge handler directly
            this.wireToggleBadgeHandler(
                this.options.containerSelector,
                selector,
                this.options.postUrl
            );

            return true;
        }, false);
    }

    /**
     * Wire toggle badge handler for grid actions
     * @param {string|HTMLElement|JQuery} containerSelector - Container selector
     * @param {string} gridSelector - Grid selector to refresh
     * @param {string} postUrl - POST URL for the action
     */
    wireToggleBadgeHandler(containerSelector, gridSelector, postUrl) {
        return safeExecute(() => {
            const $container = containerSelector ? $(containerSelector) : $(document);
            $container.off('click.kendoToggleBadge', '.badge-toggle-btn');
            $container.on('click.kendoToggleBadge', '.badge-toggle-btn', e => {
                e.preventDefault();
                const $btn = $(e.currentTarget);
                const id = $btn.data('id');
                const flag = $btn.data('flag');

                if (id === undefined || flag === undefined) {
                    return;
                }

                $btn.prop('disabled', true);

                $.ajax({
                    url: postUrl,
                    type: 'POST',
                    data: { id: id, flag: flag },
                    success: () => {
                        // Refresh the grid
                        const manager = GridManagerAPI.get(gridSelector);
                        if (manager) {
                            manager.refresh();
                        }

                        // Emit event o úspěšné operaci
                        eventBus.emit(EVENTS.GRID_TOGGLE_SUCCESS, {
                            gridSelector: gridSelector,
                            id: id,
                            flag: flag
                        });
                    },
                    error: xhr => {
                        // Check if it's a business validation error (4xx) or technical error (5xx)
                        if (xhr && xhr.status >= 500) {
                            logError(new Error('Toggle badge request failed'), {
                                xhr: xhr,
                                id: id,
                                flag: flag
                            });
                        }

                        // Always handle the response (business validation or technical error)
                        if (window.Result) {
                            window.Result.handleAjaxError(xhr);
                        }
                    },
                    complete: () => {
                        $btn.prop('disabled', false);
                    }
                });
            });
        }, 'ToggleHandlerPlugin.wireToggleBadgeHandler');
    }
}

/**
 * Filter Plugin - Handles filter management
 */
class FilterPlugin extends GridPlugin {
    constructor(options = {}) {
        super('filter', {
            enabled: true,
            autoInitialize: true,
            ...options
        });
        // Override this.enabled from options since base class ignores it
        // If filter plugin is not provided in options (undefined or empty), disable it
        // Otherwise, use options.enabled if explicitly set, default to true
        const hasOptions = options && Object.keys(options).length > 0;
        this.enabled = hasOptions
            ? options.enabled !== undefined
                ? options.enabled
                : true
            : false;
    }

    async initialize(manager, selector) {
        if (!this.enabled) {
            logDebug('FilterPlugin: Disabled');
            return false;
        }

        return safeExecute(() => {
            logDebug('FilterPlugin: Creating FilterManager', { selector });

            // Remove 'enabled' from options as FilterManager doesn't use it
            // Keep other options like autoInitialize, filterFormSelector, etc.
            // eslint-disable-next-line no-unused-vars -- enabled kept for potential future use
            const { enabled, ...filterOptions } = this.options;

            const filterManager = FilterManager.create({
                gridSelector: selector,
                ...filterOptions
            });

            // Store reference for potential future use
            manager._filterManager = filterManager;

            return true;
        }, false);
    }

    destroy() {
        if (this._filterManager) {
            this._filterManager.destroy();
        }
    }
}

/**
 * Row Click Plugin - Handles row click redirection functionality
 */
class RowClickPlugin extends GridPlugin {
    constructor(options = {}) {
        super('rowClick', {
            enabled: true,
            ...options
        });
        // Override this.enabled from options since base class ignores it
        // If rowClick plugin is not provided in options (undefined or empty), disable it
        // Otherwise, use options.enabled if explicitly set, default to true
        const hasOptions = options && Object.keys(options).length > 0;
        this.enabled = hasOptions
            ? options.enabled !== undefined
                ? options.enabled
                : true
            : false;
        this.manager = null;
    }

    async initialize(manager, selector) {
        if (!this.enabled) {
            logDebug('RowClickPlugin: Disabled');
            return false;
        }

        this.manager = manager;

        return safeExecute(() => {
            logDebug('RowClickPlugin: Activating row links', { selector });

            const grid = manager.getGrid();
            if (!grid) {
                logWarning('RowClickPlugin: Grid not found', { selector });
                return false;
            }

            this.activateRowLinks(grid);
            return true;
        }, false);
    }

    /**
     * Activate row click functionality for the grid
     * @param {Object} grid - Kendo Grid instance
     */
    activateRowLinks(grid) {
        if (!this.enabled) {
            logDebug('RowClickPlugin: Disabled');
            return;
        }

        // Determine mode by priority (redirect > callback)
        let mode = null;
        let config = null;

        if (this.options.redirect) {
            mode = 'redirect';
            config = this.options.redirect;
        } else if (this.options.callback) {
            mode = 'callback';
            config = this.options.callback;
        } else {
            logWarning('RowClickPlugin: No redirect or callback configuration provided');
            return;
        }

        // For callback mode, config is the function itself
        const rowLinkUrl = mode === 'callback' ? null : config.url;
        if (mode !== 'callback' && !rowLinkUrl) {
            logWarning('RowClickPlugin: No URL provided in configuration');
            return;
        }

        const rowLinkProperty = this.options.property || 'Id';
        const rowLinkActivatePropertyForRow = this.options.activateProperty;

        // Check if grid has selectable enabled
        this.hasSelectable = grid.options.selectable && grid.options.selectable.enabled;

        // Store mode and config for use in click handler
        this.mode = mode;
        this.config = config;

        // Process existing rows
        this.processRows(grid, rowLinkUrl, null, rowLinkProperty, rowLinkActivatePropertyForRow);

        // Listen for data bound event to process new rows
        grid.bind('dataBound', () => {
            this.processRows(
                grid,
                rowLinkUrl,
                null,
                rowLinkProperty,
                rowLinkActivatePropertyForRow
            );
        });
    }

    /**
     * Process grid rows and add click functionality
     * @param {Object} grid - Kendo Grid instance
     * @param {string} rowLinkUrl - Base URL for row links
     * @param {string} rowLinkAction - Action function name
     * @param {string} rowLinkProperty - Property name to use as ID
     * @param {string} rowLinkActivatePropertyForRow - Property to check for row activation
     */
    processRows(grid, rowLinkUrl, rowLinkAction, rowLinkProperty, rowLinkActivatePropertyForRow) {
        const $tbody = $(grid.tbody);

        $tbody.children('tr').each((index, rowElement) => {
            const $row = $(rowElement);
            const rowData = grid.dataItem(rowElement);

            if (!rowData) {
                return;
            }

            // Check if row should be activated based on property
            if (
                rowLinkActivatePropertyForRow !== undefined &&
                !rowData.get(rowLinkActivatePropertyForRow)
            ) {
                return;
            }

            // Add cursor pointer to non-action, non-editable cells (excluding cells with interactive elements)
            $row.children('td:not(.action-column):not(.editable-column)').each(
                (cellIndex, cellElement) => {
                    const $cell = $(cellElement);

                    // Skip cells that contain interactive elements
                    if ($cell.find('.badge-toggle-btn').length > 0) {
                        return;
                    }
                    // Skip cells that contain checkboxes (Kendo Grid selection)
                    if ($cell.find('input[type="checkbox"]').length > 0) {
                        return;
                    }

                    if (rowData[rowLinkProperty] !== undefined && rowData[rowLinkProperty] > 0) {
                        $cell.addClass('cursor-pointer').css('cursor', 'pointer');
                    }
                }
            );

            // Add click handler to non-action, non-editable cells (excluding cells with interactive elements)
            $row.off('click.rowClick');
            $row.on(
                'click.rowClick',
                'td:not(.action-column):not(.editable-column):not(.k-hierarchy-cell)',
                e => {
                    const $clickedCell = $(e.target).closest('td');

                    // Skip if clicked on interactive elements
                    if ($(e.target).closest('.badge-toggle-btn').length > 0) {
                        return;
                    }
                    // Skip if clicked on checkbox elements (Kendo Grid selection)
                    if ($(e.target).closest('input[type="checkbox"]').length > 0) {
                        return;
                    }

                    // Skip if the clicked cell contains interactive elements (disable rowClick for entire cell)
                    if ($clickedCell.find('.badge-toggle-btn').length > 0) {
                        return;
                    }
                    // Skip if the clicked cell contains checkboxes (Kendo Grid selection)
                    if ($clickedCell.find('input[type="checkbox"]').length > 0) {
                        return;
                    }

                    if (rowData[rowLinkProperty] === undefined || rowData[rowLinkProperty] === 0) {
                        return;
                    }

                    // If grid has selectable enabled, check if user is trying to select the row
                    if (this.hasSelectable) {
                        // Check if the click target is the checkbox or its container
                        const $checkboxCell = $row.find('td:first-child'); // First cell usually contains checkbox

                        // If clicked on checkbox cell, don't trigger rowClick
                        if ($clickedCell.is($checkboxCell)) {
                            return;
                        }
                    }

                    // Prevent default row selection behavior when rowClick is active
                    e.preventDefault();
                    e.stopPropagation();

                    // Handle based on mode
                    if (this.mode === 'callback' && typeof this.config === 'function') {
                        this.config(rowData, e);
                    } else if (this.mode === 'redirect') {
                        const url = `${rowLinkUrl}/${rowData[rowLinkProperty]}`;
                        this.openUrl(url, e);
                    }
                }
            );
        });
    }

    /**
     * Open URL with proper event handling
     * @param {string} url - URL to open
     * @param {Event} event - Click event
     */
    openUrl(url, event) {
        // Check if Ctrl/Cmd key is pressed for new tab
        if (event.ctrlKey || event.metaKey) {
            window.open(url, '_blank');
        } else {
            window.location.href = url;
        }
    }

    destroy() {
        // Clean up event handlers
        if (this.manager) {
            const grid = this.manager.getGrid();
            if (grid && grid.tbody) {
                $(grid.tbody).off('click.rowClick');
            }
        }
    }
}

/**
 * Inline Edit Plugin - Handles inline editing functionality (add row button, request end handler)
 */
class InlineEditPlugin extends GridPlugin {
    constructor(options = {}) {
        super('inlineEdit', {
            enabled: true, // Default enabled
            addRowButtonId: null,
            refreshOnCreate: true,
            ...options
        });
        // Normalize enabled to boolean (handles string "true"/"false" from Razor)
        if (typeof this.options.enabled === 'string') {
            this.options.enabled = this.options.enabled.toLowerCase() === 'true';
        }
        // If addRowButtonId is configured, plugin is enabled only if explicitly enabled
        // Otherwise, enable plugin if refreshOnCreate is enabled
        if (this.options.addRowButtonId) {
            // If button is configured, use explicit enabled flag
            this.enabled = this.options.enabled !== false;
        } else {
            // If no button, enable plugin if refreshOnCreate is enabled
            this.enabled = this.options.enabled !== false && this.options.refreshOnCreate;
        }
        this.manager = null;
        this.selector = null;
    }

    async initialize(manager, selector) {
        if (!this.enabled) {
            logDebug('InlineEditPlugin: Disabled');
            return false;
        }

        this.manager = manager;
        this.selector = selector;

        return safeExecute(() => {
            logDebug('InlineEditPlugin: Initializing', {
                selector,
                addRowButtonId: this.options.addRowButtonId,
                enabled: this.options.enabled,
                refreshOnCreate: this.options.refreshOnCreate
            });

            // Initialize add row button if configured and enabled
            if (this.options.addRowButtonId && this.options.enabled) {
                this.initializeAddRowButton();
            }

            // Setup request end handler if configured
            if (this.options.refreshOnCreate) {
                this.setupRequestEndHandler();
            }

            // Setup error handler for inline editing validation errors
            this.setupErrorHandler();

            return true;
        }, false);
    }

    /**
     * Initialize add row button functionality
     */
    initializeAddRowButton() {
        return safeExecute(() => {
            if (!this.options.enabled || !this.options.addRowButtonId) {
                return;
            }

            const button = document.getElementById(this.options.addRowButtonId);
            if (!button) {
                logWarning('InlineEditPlugin: Add row button not found', {
                    buttonId: this.options.addRowButtonId
                });
                return;
            }

            // Remove existing listeners if handler already exists
            if (this.handleAddRowClick) {
                button.removeEventListener('click', this.handleAddRowClick);
            }

            // Create and store bound handler
            this.handleAddRowClick = () => {
                const grid = this.manager.getGrid();
                if (grid && typeof grid.addRow === 'function') {
                    grid.addRow();
                } else {
                    logWarning('InlineEditPlugin: Grid addRow method not available', {
                        selector: this.selector,
                        gridAvailable: !!grid,
                        hasAddRow: grid && typeof grid.addRow === 'function'
                    });
                }
            };

            button.addEventListener('click', this.handleAddRowClick);

            logDebug('InlineEditPlugin: Add row button initialized', {
                buttonId: this.options.addRowButtonId,
                selector: this.selector
            });
        }, 'InlineEditPlugin.initializeAddRowButton');
    }

    /**
     * Setup request end handler for grid data source
     */
    setupRequestEndHandler() {
        return safeExecute(() => {
            const grid = this.manager.getGrid();
            if (!grid || !grid.dataSource) {
                logWarning('InlineEditPlugin: Grid or dataSource not available', {
                    selector: this.selector
                });
                return;
            }

            // Create handler function
            const handler = e => {
                if (e.type === 'create' && !e.response?.Errors && this.options.refreshOnCreate) {
                    // Refresh grid to apply sorting and show new item in correct position
                    const managerGrid = this.manager.getGrid();
                    if (managerGrid && managerGrid.dataSource) {
                        managerGrid.dataSource.read();
                    }
                }
            };

            // Bind handler directly to dataSource requestEnd event
            grid.dataSource.bind('requestEnd', handler);

            // Store handler reference for cleanup
            this.requestEndHandler = handler;

            logDebug('InlineEditPlugin: Request end handler attached', {
                selector: this.selector
            });
        }, 'InlineEditPlugin.setupRequestEndHandler');
    }

    /**
     * Setup error handler for DataSource to display validation errors
     */
    setupErrorHandler() {
        return safeExecute(() => {
            const grid = this.manager.getGrid();
            if (!grid || !grid.dataSource) {
                logWarning('InlineEditPlugin: Grid or dataSource not available for error handler', {
                    selector: this.selector
                });
                return;
            }

            // Create error handler function
            const errorHandler = args => {
                if (args.errors) {
                    // Prevent grid from exiting edit mode
                    grid.one('dataBinding', e => e.preventDefault());

                    // Extract error messages from ModelState format
                    const messages = [];
                    if (typeof args.errors === 'object') {
                        // ModelState errors format: { "": { errors: ["message"] }, "FieldName": { errors: ["message"] } }
                        for (const key in args.errors) {
                            const fieldErrors = args.errors[key];
                            if (fieldErrors?.errors && Array.isArray(fieldErrors.errors)) {
                                messages.push(...fieldErrors.errors);
                            }
                        }
                    }

                    // Show error via toastr
                    const message = messages.join('\n') || 'Chyba při ukládání záznamu';
                    if (typeof toastr !== 'undefined') {
                        toastr.error(message);
                    } else {
                        logWarning('InlineEditPlugin: toastr not available, error message:', message);
                    }

                    logDebug('InlineEditPlugin: Validation error displayed', {
                        selector: this.selector,
                        errors: args.errors,
                        message: message
                    });
                }
            };

            // Bind error handler to dataSource
            grid.dataSource.bind('error', errorHandler);

            // Store handler reference for cleanup
            this.errorHandler = errorHandler;

            logDebug('InlineEditPlugin: Error handler attached', {
                selector: this.selector
            });
        }, 'InlineEditPlugin.setupErrorHandler');
    }

    /**
     * Clean up plugin resources
     */
    destroy() {
        return safeExecute(() => {
            // Clean up add row button listener
            if (this.options.addRowButtonId && this.handleAddRowClick) {
                const button = document.getElementById(this.options.addRowButtonId);
                if (button) {
                    button.removeEventListener('click', this.handleAddRowClick);
                }
            }

            // Clean up request end handler from dataSource
            if (this.requestEndHandler) {
                const grid = this.manager?.getGrid();
                if (grid && grid.dataSource) {
                    grid.dataSource.unbind('requestEnd', this.requestEndHandler);
                }
            }

            // Clean up error handler from dataSource
            if (this.errorHandler) {
                const grid = this.manager?.getGrid();
                if (grid && grid.dataSource) {
                    grid.dataSource.unbind('error', this.errorHandler);
                }
            }

            // Clean up references
            this.manager = null;
            this.selector = null;
            this.handleAddRowClick = null;
            this.requestEndHandler = null;
            this.errorHandler = null;
        }, 'InlineEditPlugin.destroy');
    }
}

/**
 * Delete Plugin - Handles row deletion with confirmation dialog
 */
class DeletePlugin extends GridPlugin {
    constructor(options = {}) {
        super('delete', {
            enabled: false,
            property: 'Id',
            nameProperty: 'Name',
            fallbackNameProperties: ['Name', 'Title', 'Abbrv'],
            title: 'Smazání záznamu',
            message: 'Opravdu chcete smazat záznam?',
            ...options
        });
        // Override this.enabled from options since base class ignores it
        this.enabled = this.options.enabled;
        this.manager = null;
        this.selector = null;
    }

    async initialize(manager, selector) {
        if (!this.enabled || !this.options.url) {
            logDebug('DeletePlugin: Disabled or no URL provided');
            return false;
        }

        this.manager = manager;
        this.selector = selector;

        return safeExecute(() => {
            logDebug('DeletePlugin: Wiring delete handlers', {
                gridSelector: selector,
                url: this.options.url
            });

            this.wireDeleteHandler(selector, this.options);

            return true;
        }, false);
    }

    /**
     * Wire delete button handler for grid actions
     * @param {string} gridSelector - Grid selector (used both as container and grid identifier)
     * @param {Object} options - Plugin options
     */
    wireDeleteHandler(gridSelector, options) {
        return safeExecute(() => {
            const $container = gridSelector ? $(gridSelector) : $(document);

            // Remove existing handlers to prevent duplicates
            $container.off('click.kendoDelete', '.js-grid-delete');

            $container.on('click.kendoDelete', '.js-grid-delete', e => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                const $btn = $(e.currentTarget);

                // Get dataItem from grid row
                let dataItem = null;
                let id = undefined;
                const grid = this.manager ? this.manager.getGrid() : null;
                if (grid) {
                    const $row = $btn.closest('tr');
                    if ($row.length > 0) {
                        dataItem = grid.dataItem($row);

                        // Get ID from dataItem using configured property
                        if (dataItem && options.property) {
                            id = dataItem[options.property];
                        }
                    }
                }

                if (id === undefined) {
                    logWarning('DeletePlugin: No ID found in grid row');
                    return;
                }

                this.showDeleteConfirmation(id, $btn, gridSelector, options, dataItem);
            });
        }, 'DeletePlugin.wireDeleteHandler');
    }

    /**
     * Show confirmation dialog for deletion
     * @param {*} id - Record ID
     * @param {jQuery} $btn - Delete button element (null for inline editing)
     * @param {string} gridSelector - Grid selector
     * @param {Object} options - Plugin options
     * @param {Object} dataItem - Optional dataItem for displaying name
     */
    showDeleteConfirmation(id, $btn, gridSelector, options, dataItem = null) {
        return safeExecute(() => {
            // Build message with name if dataItem is available
            let message = options.message || '';

            // Remove trailing question mark if present (we'll add it programmatically)
            message = message.trim().replace(/\?$/, '');

            // Insert name into message if available
            if (dataItem) {
                let name = null;

                // Try options.nameProperty first
                if (options.nameProperty && dataItem[options.nameProperty]) {
                    name = dataItem[options.nameProperty];
                }
                // Fallback to configured fallback properties
                else if (
                    options.fallbackNameProperties &&
                    Array.isArray(options.fallbackNameProperties)
                ) {
                    for (const prop of options.fallbackNameProperties) {
                        if (dataItem[prop]) {
                            name = dataItem[prop];
                            break;
                        }
                    }
                }

                if (name) {
                    message = `${message} <strong>${name}</strong>`;
                }
            }

            // Add question mark at the end unless disabled (e.g. multi-sentence messages ending with a period)
            if (options.appendQuestionMark !== false) {
                message = `${message}?`;
            }

            // Use confirmation dialog module
            const $modal = showDangerConfirmationDialog({
                title: options.title || 'Smazání záznamu',
                message: `<p>${message}</p>`,
                confirmText: 'Smazat',
                cancelText: 'Zrušit',
                onConfirm: () => {
                    this.performDelete(id, $btn, gridSelector, options);
                }
            });

            // Store modal reference for potential disabling buttons
            this._currentModal = $modal;
        }, 'DeletePlugin.showDeleteConfirmation');
    }

    /**
     * Perform the actual delete operation
     * @param {*} id - Record ID
     * @param {jQuery} $btn - Delete button element (null for inline editing)
     * @param {string} gridSelector - Grid selector
     * @param {Object} options - Plugin options
     */
    performDelete(id, $btn, gridSelector, options) {
        return safeExecute(() => {
            GridPlugin.ajaxAction(
                { url: options.url, type: 'POST', data: { id: id } },
                {
                    $btn,
                    onSuccess: result => {
                        // Remove row from grid or refresh grid
                        const $row = $btn ? $btn.closest('tr') : null;
                        const manager = GridManagerAPI.get(gridSelector);
                        if (manager) {
                            const grid = manager.getGrid();
                            if (grid) {
                                if ($row && typeof grid.removeRow === 'function') {
                                    grid.removeRow($row);
                                } else if (grid.dataSource) {
                                    grid.dataSource.read();
                                }
                            }
                        }

                        // Emit success event
                        eventBus.emit(EVENTS.GRID_DELETE_SUCCESS, {
                            gridSelector: gridSelector,
                            id: id,
                            row: $row
                        });

                        // Call custom success callback if provided
                        if (options.onSuccess && typeof options.onSuccess === 'function') {
                            options.onSuccess(id, $row, result);
                        }
                    }
                }
            );
        }, 'DeletePlugin.performDelete');
    }

    /**
     * Clean up plugin resources
     */
    destroy() {
        return safeExecute(() => {
            // Clean up references
            this.manager = null;
            this.selector = null;
        }, 'DeletePlugin.destroy');
    }
}

/**
 * Bulk Selection Plugin - Handles bulk row selection with info bar and actions
 *
 * Provides:
 * - Selection tracking across pages via PersistSelection + selectedKeyNames()
 * - "Select all filtered" mode (sends selectAll flag to server instead of IDs)
 * - Visual info bar showing selection count and available actions
 * - Automatic selection clearing on filter change
 * - Configurable bulk action buttons with confirmation dialogs
 */
class BulkSelectionPlugin extends GridPlugin {
    constructor(options = {}) {
        super('bulkSelection', {
            enabled: false,
            selectAllEnabled: true,
            infoBarSelector: null,
            actions: [],
            getFilterValues: () => (window.getFilterValues ? window.getFilterValues() : {}),
            texts: {
                allSelected: count => `<strong>Vybrány všechny záznamy (${count}).</strong>`,
                nSelected: (count, word) => `Vybráno <strong>${count}</strong> ${word}.`,
                selectAllFiltered: count => `Vybrat všech ${count} záznamů odpovídajících filtru`,
                clearSelection: 'Zrušit výběr',
                confirmTitle: 'Potvrzení hromadné akce',
                confirmText: 'Potvrdit',
                cancelText: 'Zrušit'
            },
            ...options
        });
        this.enabled = this.options.enabled;
        this.manager = null;
        this.selector = null;
        this._selectAll = false;
        this._excludeIds = new Set();
        this._suppressChangeHandler = false;
        this._lastEmittedState = null;
        this._infoBar = null;
        this._$selectionInfo = null;
        this._$selectAllLink = null;
        this._$clearLink = null;
        this._$bulkActions = null;
        this._changeHandler = null;
        this._dataBoundHandler = null;
        this._filterAppliedUnsub = null;
        this._filterClearedUnsub = null;
    }

    async initialize(manager, selector) {
        if (!this.enabled) {
            logDebug('BulkSelectionPlugin: Disabled');
            return false;
        }

        this.manager = manager;
        this.selector = selector;

        return safeExecute(() => {
            logDebug('BulkSelectionPlugin: Initializing', { selector });

            this._createInfoBar();
            this._bindGridEvents();
            this._bindFilterEvents();

            return true;
        }, false);
    }

    // ───────────────────────── Info bar ─────────────────────────

    /**
     * Create and inject the selection info bar into the DOM
     */
    _createInfoBar() {
        const grid = this.manager.getGrid();
        if (!grid) return;

        // Build action buttons HTML from config
        const actionsHtml = (this.options.actions || [])
            .map(
                action => `
                <button type="button"
                    class="btn btn-sm ${action.cssClass || 'btn-primary'} bulk-action-btn ml-2"
                    data-action="${action.name}"
                    data-url="${action.url || ''}"
                    data-confirm="${action.confirm || ''}">
                    ${action.icon ? `<i class="${action.icon} mr-1"></i>` : ''}${action.text}
                </button>`
            )
            .join('');

        const html = `
            <div class="bulk-selection-bar alert alert-info align-items-center py-2 px-3 d-none">
                <span class="selection-info mr-2"></span>
                <a href="#" class="select-all-link d-none mr-2"></a>
                <a href="#" class="clear-selection-link d-none mr-2"></a>
                <div class="bulk-actions ml-auto">${actionsHtml}</div>
            </div>`;

        if (this.options.infoBarSelector) {
            this._infoBar = $(this.options.infoBarSelector);
            this._infoBar.html(html);
            this._infoBar = this._infoBar.find('.bulk-selection-bar');
        } else {
            // Inject inside the grid, between header and content
            const $grid = $(this.selector);
            $grid.find('.k-grid-header').after(html);
            this._infoBar = $grid.find('.bulk-selection-bar');
        }

        // Wire info bar click handlers
        this._infoBar.on('click', '.select-all-link', e => {
            e.preventDefault();
            this.selectAllFiltered();
        });

        this._infoBar.on('click', '.clear-selection-link', e => {
            e.preventDefault();
            this.clearSelection();
        });

        this._infoBar.on('click', '.bulk-action-btn', e => {
            e.preventDefault();
            const $btn = $(e.currentTarget);
            const actionName = $btn.data('action');
            const action = (this.options.actions || []).find(a => a.name === actionName);
            if (action) {
                this._executeBulkAction(action, $btn);
            }
        });

        // Cache DOM element references for faster lookups in _updateInfoBar
        this._$selectionInfo = this._infoBar.find('.selection-info');
        this._$selectAllLink = this._infoBar.find('.select-all-link');
        this._$clearLink = this._infoBar.find('.clear-selection-link');
        this._$bulkActions = this._infoBar.find('.bulk-actions');
    }

    /**
     * Update the info bar based on current selection state
     */
    _updateInfoBar() {
        if (!this._infoBar) return;

        const grid = this.manager.getGrid();
        if (!grid) return;

        const selectedIds = grid.selectedKeyNames ? grid.selectedKeyNames() : [];
        const selectedCount = selectedIds.length;
        const total = grid.dataSource ? grid.dataSource.total() : 0;
        const texts = this.options.texts;

        if (this._selectAll) {
            // "All filtered" mode (possibly with exclusions)
            const effectiveCount = total - this._excludeIds.size;
            this._infoBar.removeClass('d-none').addClass('d-flex');
            this._$selectionInfo.html(
                this._excludeIds.size === 0
                    ? texts.allSelected(total)
                    : texts.nSelected(effectiveCount, this._pluralizeRecords(effectiveCount))
            );
            // Show "re-select all" link when some are excluded
            if (this._excludeIds.size > 0) {
                this._$selectAllLink.removeClass('d-none')
                    .text(texts.selectAllFiltered(total));
            } else {
                this._$selectAllLink.addClass('d-none');
            }
            this._$clearLink.removeClass('d-none').text(texts.clearSelection);
            this._$bulkActions.removeClass('d-none');
        } else if (selectedCount > 0) {
            // Individual selection mode
            this._infoBar.removeClass('d-none').addClass('d-flex');
            const recordWord = this._pluralizeRecords(selectedCount);
            this._$selectionInfo.html(texts.nSelected(selectedCount, recordWord));

            // Show "select all" link if there are more total records than selected
            if (this.options.selectAllEnabled && total > selectedCount) {
                this._$selectAllLink
                    .removeClass('d-none')
                    .text(texts.selectAllFiltered(total));
            } else {
                this._$selectAllLink.addClass('d-none');
            }

            this._$clearLink.removeClass('d-none').text(texts.clearSelection);
            this._$bulkActions.removeClass('d-none');
        } else {
            // No selection
            this._infoBar.removeClass('d-flex').addClass('d-none');
        }

        // Only emit event when selection state actually changes
        const excludeIds = [...this._excludeIds];
        const newState = JSON.stringify({ selectedIds, selectAll: this._selectAll, excludeIds, total });
        if (newState !== this._lastEmittedState) {
            this._lastEmittedState = newState;
            eventBus.emit(EVENTS.GRID_SELECTION_CHANGED, {
                gridSelector: this.selector,
                selectedIds,
                excludeIds,
                selectAll: this._selectAll,
                total
            });
        }
    }

    /**
     * Czech pluralization for "záznam/záznamy/záznamů"
     * @param {number} count
     * @returns {string}
     */
    _pluralizeRecords(count) {
        if (count === 1) return 'záznam';
        if (count >= 2 && count <= 4) return 'záznamy';
        return 'záznamů';
    }

    // ───────────────────────── Event binding ─────────────────────────

    /**
     * Bind to grid change (selection) and dataBound events
     */
    _bindGridEvents() {
        const grid = this.manager.getGrid();
        if (!grid) return;

        // Grid change event fires on selection change
        this._changeHandler = () => {
            // Skip handler during programmatic selection (e.g. selectAllFiltered)
            if (this._suppressChangeHandler) return;

            if (this._selectAll) {
                // In "select all" mode: track which rows are deselected as exclusions
                const g = this.manager.getGrid();
                if (g) {
                    const view = g.dataSource.view();
                    const selectedKeys = new Set(g.selectedKeyNames());

                    for (const item of view) {
                        const key = String(item.Id);
                        if (selectedKeys.has(key)) {
                            // Row is selected -> remove from excludes (re-selected)
                            this._excludeIds.delete(key);
                        } else {
                            // Row is deselected -> add to excludes
                            this._excludeIds.add(key);
                        }
                    }
                }
                this._updateInfoBar();
                return;
            }

            this._updateInfoBar();
        };
        grid.bind('change', this._changeHandler);

        // dataBound event - update info bar after data loads (e.g. page change)
        // When in "select all" mode, visually check all rows except excluded ones
        this._dataBoundHandler = () => {
            if (this._selectAll) {
                this._suppressChangeHandler = true;
                const g = this.manager.getGrid();
                if (g) {
                    const rows = g.items();
                    if (rows && rows.length > 0) {
                        if (this._excludeIds.size === 0) {
                            // No exclusions: select all rows at once
                            g.select(rows);
                        } else {
                            // Select only rows NOT in excludeIds
                            const view = g.dataSource.view();
                            rows.each((i, row) => {
                                const item = view[i];
                                if (item && !this._excludeIds.has(String(item.Id))) {
                                    g.select($(row));
                                }
                            });
                        }
                    }
                }
                this._suppressChangeHandler = false;
            }
            this._updateInfoBar();
        };
        grid.bind('dataBound', this._dataBoundHandler);
    }

    /**
     * Bind to filter events to clear selection when filters change
     */
    _bindFilterEvents() {
        this._filterAppliedUnsub = eventBus.on(EVENTS.FILTER_APPLIED, () => {
            this._onFilterChanged();
        });

        this._filterClearedUnsub = eventBus.on(EVENTS.FILTER_CLEARED, () => {
            this._onFilterChanged();
        });
    }

    /**
     * Handle filter change - clear all selection state
     */
    _onFilterChanged() {
        this._selectAll = false;
        this._excludeIds = new Set();
        this._clearGridSelection();
        this._updateInfoBar();
    }

    /**
     * Clear the grid's visual selection state, PersistSelection internal IDs, and header checkbox.
     * Shared helper used by both _onFilterChanged and clearSelection.
     */
    _clearGridSelection() {
        const grid = this.manager.getGrid();
        if (grid) {
            if (typeof grid.clearSelection === 'function') {
                grid.clearSelection();
            }
            // Clear PersistSelection internal state (cross-page selected IDs)
            if (grid._selectedIds) {
                grid._selectedIds = {};
            }
            // Uncheck header "select all" checkbox
            $(grid.thead).find('.k-checkbox-md').prop('checked', false);
        }
    }

    // ───────────────────────── Bulk actions ─────────────────────────

    /**
     * Execute a bulk action with optional confirmation
     * @param {Object} action - Action configuration
     * @param {jQuery} $btn - Button element
     */
    _executeBulkAction(action, $btn) {
        const texts = this.options.texts;
        const state = this.getSelectionState();

        // Get current filter values via configurable callback
        const filterValues = this.options.getFilterValues();

        const requestData = {
            ...filterValues,
            selectedIds: state.selectAll ? null : state.selectedIds,
            excludeIds: state.selectAll ? state.excludeIds : null,
            selectAll: state.selectAll,
            ...(action.data || {})
        };

        // ── Handler-based action (JS-only, e.g. clipboard, open URL) ──
        if (typeof action.handler === 'function') {
            const executeHandler = async () => {
                try {
                    $btn.prop('disabled', true);
                    await action.handler({
                        requestData,
                        selectionState: state,
                        clearSelection: () => this.clearSelection(),
                        refresh: () => this.manager.refresh()
                    });
                } finally {
                    $btn.prop('disabled', false);
                }
            };

            if (action.confirm) {
                showWarningConfirmationDialog({
                    title: action.confirmTitle || texts.confirmTitle,
                    message: `<p>${action.confirm}</p>`,
                    confirmText: action.text || texts.confirmText,
                    cancelText: texts.cancelText,
                    onConfirm: executeHandler
                });
            } else {
                executeHandler();
            }
            return;
        }

        // ── Standard server-side action (POST + refresh) ──
        const execute = () => {
            GridPlugin.ajaxAction(
                {
                    url: action.url,
                    type: 'POST',
                    contentType: 'application/json',
                    data: JSON.stringify(requestData)
                },
                {
                    $btn,
                    onSuccess: result => {
                        // Clear selection and refresh grid
                        this.clearSelection();
                        this.manager.refresh();

                        // Call custom success callback if provided
                        if (action.onSuccess && typeof action.onSuccess === 'function') {
                            action.onSuccess(result);
                        }
                    }
                }
            );
        };

        if (action.confirm) {
            showWarningConfirmationDialog({
                title: action.confirmTitle || texts.confirmTitle,
                message: `<p>${action.confirm}</p>`,
                confirmText: action.text || texts.confirmText,
                cancelText: texts.cancelText,
                onConfirm: execute
            });
        } else {
            execute();
        }
    }

    // ───────────────────────── Public API ─────────────────────────

    /**
     * Get current selection state
     * @returns {{ selectedIds: string[], selectAll: boolean, count: number, total: number }}
     */
    getSelectionState() {
        const grid = this.manager.getGrid();
        const selectedIds = grid && grid.selectedKeyNames ? grid.selectedKeyNames() : [];
        const total = grid && grid.dataSource ? grid.dataSource.total() : 0;
        const excludeIds = [...this._excludeIds].map(Number);

        return {
            selectedIds,
            excludeIds,
            selectAll: this._selectAll,
            count: this._selectAll ? total - this._excludeIds.size : selectedIds.length,
            total
        };
    }

    /**
     * Enter "select all filtered" mode
     */
    selectAllFiltered() {
        this._selectAll = true;
        this._excludeIds = new Set();

        // Suppress change handler during programmatic selection to prevent
        // the synchronous 'change' event from resetting _selectAll
        this._suppressChangeHandler = true;

        // Visually select all rows on current page for UX feedback
        const grid = this.manager.getGrid();
        if (grid) {
            const rows = grid.items();
            if (rows && rows.length > 0) {
                grid.select(rows);
            }
        }

        this._suppressChangeHandler = false;
        this._updateInfoBar();
    }

    /**
     * Clear all selection (both individual and "select all")
     */
    clearSelection() {
        this._selectAll = false;
        this._excludeIds = new Set();
        this._suppressChangeHandler = true;
        this._clearGridSelection();
        this._suppressChangeHandler = false;
        this._updateInfoBar();
    }

    /**
     * Check if any items are selected
     * @returns {boolean}
     */
    hasSelection() {
        const state = this.getSelectionState();
        return state.selectAll || state.selectedIds.length > 0;
    }

    // ───────────────────────── Cleanup ─────────────────────────

    destroy() {
        return safeExecute(() => {
            const grid = this.manager?.getGrid();
            if (grid) {
                if (this._changeHandler) grid.unbind('change', this._changeHandler);
                if (this._dataBoundHandler) grid.unbind('dataBound', this._dataBoundHandler);
            }

            if (this._filterAppliedUnsub) this._filterAppliedUnsub();
            if (this._filterClearedUnsub) this._filterClearedUnsub();

            if (this._infoBar) {
                this._infoBar.off('click');
                this._infoBar.remove();
            }

            this.manager = null;
            this.selector = null;
            this._excludeIds = null;
            this._infoBar = null;
            this._$selectionInfo = null;
            this._$selectAllLink = null;
            this._$clearLink = null;
            this._$bulkActions = null;
            this._lastEmittedState = null;
            this._changeHandler = null;
            this._dataBoundHandler = null;
            this._filterAppliedUnsub = null;
            this._filterClearedUnsub = null;
        }, 'BulkSelectionPlugin.destroy');
    }
}

/**
 * Main GridManager class
 */
export { GridPlugin };
export class GridManager {
    /**
     * Create a new GridManager instance
     * @param {string} selector - Grid selector
     * @param {Object} options - Configuration options
     * @returns {GridManager} - New GridManager instance
     */
    static create(selector, options = {}) {
        return new GridManager(selector, options);
    }

    /**
     * Ensure a grid is managed by GridManager
     * @param {string} selector - Grid selector
     * @param {Object} options - Configuration options
     * @returns {GridManager} - GridManager instance
     */
    static ensure(selector, options = {}) {
        // Check if already managed
        const existing = GridManager._managedGrids.get(selector);
        if (existing && !existing.isDestroyed) {
            return existing;
        }

        return new GridManager(selector, options);
    }

    constructor(selector, options = {}) {
        this.selector = selector;
        this.options = {
            autoInitialize: true,
            plugins: {},
            ...options
        };

        this.plugins = new Map();
        this.isInitialized = false;
        this.isDestroyed = false;
        this._filterManager = null;

        // Register globally
        GridManager._managedGrids.set(selector, this);

        // Auto-initialize if enabled
        if (this.options.autoInitialize) {
            this.initialize();
        }
    }

    /**
     * Initialize the GridManager and all enabled plugins
     */
    async initialize() {
        if (this.isInitialized || this.isDestroyed) {
            return;
        }

        try {
            logDebug('GridManager: Initializing', { selector: this.selector });

            // Wait for grid to be available
            await this.waitForGrid();

            // Initialize built-in plugins
            await this.initializeBuiltInPlugins();

            // Initialize custom plugins
            await this.initializeCustomPlugins();

            this.isInitialized = true;

            // Emit ready event
            eventBus.emit(EVENTS.GRID_MANAGER_READY, {
                selector: this.selector,
                manager: this
            });

            logDebug('GridManager: Initialized successfully', { selector: this.selector });
        } catch (error) {
            logError(error, { context: 'GridManager.initialize', selector: this.selector });
            eventBus.emit(EVENTS.GRID_MANAGER_ERROR, {
                selector: this.selector,
                error: error
            });
        }
    }

    /**
     * Wait for grid to be available
     */
    async waitForGrid() {
        const maxAttempts = CONFIG.GRID.MAX_WAIT_ATTEMPTS;
        let attempts = 0;

        while (attempts < maxAttempts) {
            const grid = $(this.selector).data('kendoGrid');
            if (grid) {
                this.grid = grid;
                return;
            }

            await new Promise(resolve => setTimeout(resolve, 100));
            attempts++;
        }

        throw new Error(`Grid not found for selector: ${this.selector}`);
    }

    /**
     * Initialize built-in plugins
     */
    async initializeBuiltInPlugins() {
        // FilterPlugin must run before PersistencePlugin so FilterManager attaches to the grid
        // before the initial dataSource.read() - otherwise filter values (e.g. SeasonIds) may
        // not be available when getFilterValues runs during the first read
        const builtInPlugins = [
            new FilterPlugin(this.options.plugins.filter),
            new PersistencePlugin(this.options.plugins.persistence),
            new ToggleHandlerPlugin(this.options.plugins.toggleHandler),
            new RowClickPlugin(this.options.plugins.rowClick),
            new DeletePlugin(this.options.plugins.delete),
            new InlineEditPlugin(this.options.plugins.inlineEdit),
            new BulkSelectionPlugin(this.options.plugins.bulkSelection)
        ];

        for (const plugin of builtInPlugins) {
            if (plugin.enabled) {
                try {
                    const success = await plugin.initialize(this, this.selector);
                    if (success) {
                        this.plugins.set(plugin.name, plugin);
                        logDebug(`GridManager: Plugin ${plugin.name} initialized`, {
                            selector: this.selector
                        });
                    }
                } catch (error) {
                    logError(error, {
                        context: `GridManager.initializePlugin.${plugin.name}`,
                        selector: this.selector
                    });
                }
            }
        }
    }

    /**
     * Initialize custom plugins
     */
    async initializeCustomPlugins() {
        if (!this.options.customPlugins) {
            return;
        }

        for (const [name, pluginConfig] of Object.entries(this.options.customPlugins)) {
            try {
                const plugin = new pluginConfig.class(pluginConfig.options);
                const success = await plugin.initialize(this, this.selector);
                if (success) {
                    this.plugins.set(name, plugin);
                    logDebug(`GridManager: Custom plugin ${name} initialized`, {
                        selector: this.selector
                    });
                }
            } catch (error) {
                logError(error, {
                    context: `GridManager.initializeCustomPlugin.${name}`,
                    selector: this.selector
                });
            }
        }
    }

    /**
     * Add a custom plugin
     * @param {string} name - Plugin name
     * @param {GridPlugin} plugin - Plugin instance
     */
    addPlugin(name, plugin) {
        if (this.isDestroyed) {
            logWarning('Cannot add plugin to destroyed GridManager');
            return;
        }

        this.plugins.set(name, plugin);

        if (this.isInitialized) {
            plugin.initialize(this, this.selector);
        }
    }

    /**
     * Remove a plugin
     * @param {string} name - Plugin name
     */
    removePlugin(name) {
        const plugin = this.plugins.get(name);
        if (plugin) {
            plugin.destroy();
            this.plugins.delete(name);
        }
    }

    /**
     * Get a plugin by name
     * @param {string} name - Plugin name
     * @returns {GridPlugin|null} - Plugin instance or null
     */
    getPlugin(name) {
        return this.plugins.get(name) || null;
    }

    /**
     * Enable/disable a plugin
     * @param {string} name - Plugin name
     * @param {boolean} enabled - Enable/disable flag
     */
    setPluginEnabled(name, enabled) {
        const plugin = this.plugins.get(name);
        if (plugin) {
            plugin.setEnabled(enabled);
        }
    }

    /**
     * Get grid instance
     * @returns {Object|null} - Kendo Grid instance
     */
    getGrid() {
        return this.grid || null;
    }

    /**
     * Refresh the grid
     */
    refresh() {
        if (this.grid && this.grid.dataSource) {
            this.grid.dataSource.read();
            eventBus.emit(EVENTS.GRID_REFRESHED, { selector: this.selector });
        }
    }

    /**
     * Get selected rows
     * @returns {Array} - Array of selected data items
     */
    getSelectedRows() {
        if (!this.grid || typeof this.grid.select !== 'function') {
            return [];
        }

        const selectedElements = this.grid.select();
        return selectedElements.map(element => this.grid.dataItem(element));
    }

    /**
     * Destroy the GridManager and clean up resources
     */
    destroy() {
        if (this.isDestroyed) {
            return;
        }

        logDebug('GridManager: Destroying', { selector: this.selector });

        // Destroy all plugins
        for (const [name, plugin] of this.plugins) {
            try {
                plugin.destroy();
            } catch (error) {
                logError(error, {
                    context: `GridManager.destroyPlugin.${name}`,
                    selector: this.selector
                });
            }
        }

        this.plugins.clear();
        this.isDestroyed = true;
        this.isInitialized = false;

        // Remove from managed grids
        GridManager._managedGrids.delete(this.selector);

        eventBus.emit(EVENTS.GRID_MANAGER_DESTROYED, {
            selector: this.selector
        });
    }
}

// Static registry for managed grids
GridManager._managedGrids = new Map();

// Export convenience functions
export const GridManagerAPI = {
    /**
     * Create or get existing GridManager
     * @param {string} selector - Grid selector
     * @param {Object} options - Configuration options
     * @returns {GridManager} - GridManager instance
     */
    ensure: (selector, options = {}) => GridManager.ensure(selector, options),

    /**
     * Create new GridManager
     * @param {string} selector - Grid selector
     * @param {Object} options - Configuration options
     * @returns {GridManager} - GridManager instance
     */
    create: (selector, options = {}) => GridManager.create(selector, options),

    /**
     * Get managed grid by selector
     * @param {string} selector - Grid selector
     * @returns {GridManager|null} - GridManager instance or null
     */
    get: selector => GridManager._managedGrids.get(selector) || null,

    /**
     * Get all managed grids
     * @returns {Map} - Map of all managed grids
     */
    getAll: () => GridManager._managedGrids,

    /**
     * Destroy all managed grids
     */
    destroyAll: () => {
        for (const [, manager] of GridManager._managedGrids) {
            manager.destroy();
        }
    }
};

export default GridManager;
